"use client";

import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Calculator, Loader2, Plus, Save } from "lucide-react";

interface BAPBValuationTestingProps {
  onBack?: () => void;
  setActiveSection?: (section: string) => void;
}

interface GainLossInPnl {
  closing_balance: number;
  opening_balance: number;
  acquisitions: number;
  sale_or_disposal: number;
  gain_loss: number;
  gain_loss_tb?: string;
  difference?: number | string;
  user_rows?: Array<{ reason: string; amount: number }> | Record<string, any>;
  formula?: string;
}

interface ValuationTestingJson {
  valuation_testing?: {
    auditor_used_specialist?: { used_specialist?: string };
    gain_loss_in_pnl?: GainLossInPnl;
  };
}

interface LedgerOption {
  name: string;
  amount: number;
}

export default function BAPBValuationTesting({ onBack }: BAPBValuationTestingProps) {
  const [auditorUsedSpecialist, setAuditorUsedSpecialist] = useState<"" | "Yes" | "No">("");
  const [gainLossData, setGainLossData] = useState<GainLossInPnl | null>(null);
  const [fsOptions, setFsOptions] = useState<LedgerOption[]>([]);
  const [selectedLedgers, setSelectedLedgers] = useState<string[]>([]);
  const [isLedgerDropdownOpen, setIsLedgerDropdownOpen] = useState(false);
  const [userRows, setUserRows] = useState<Array<{ reason: string; amount: number }>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");

  const numericDifference = useMemo(() => {
    if (!gainLossData) return null;
    const baseGain = Number(gainLossData.gain_loss || 0);
    if (!fsOptions.length || !selectedLedgers.length) return null;
    const totalLedger = fsOptions
      .filter((o) => selectedLedgers.includes(o.name))
      .reduce((sum, o) => sum + (Number(o.amount) || 0), 0);
    return totalLedger - baseGain;
  }, [gainLossData, fsOptions, selectedLedgers]);

  const userRowsSum = useMemo(
    () => userRows.reduce((s, r) => s + (Number(r.amount) || 0), 0),
    [userRows]
  );

  const netDifference = useMemo(() => {
    if (numericDifference == null) return null;
    return numericDifference - userRowsSum;
  }, [numericDifference, userRowsSum]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const jsonResult = await (window as any).sharePointAPI?.downloadClientJson?.(
          "Execution_BAPB_BioAst.json"
        );
        if (jsonResult?.success && jsonResult?.data) {
          const data: ValuationTestingJson = jsonResult.data;
          const gl = data.valuation_testing?.gain_loss_in_pnl;
          if (gl) {
            setGainLossData(gl);
            if (typeof gl.gain_loss_tb === "string" && gl.gain_loss_tb.trim()) {
              const parts = gl.gain_loss_tb
                .split(",")
                .map((p) => p.trim())
                .filter(Boolean);
              setSelectedLedgers(parts);
            }
            const rowsRaw = gl.user_rows;
            if (Array.isArray(rowsRaw)) {
              setUserRows(
                rowsRaw.map((r: any) => ({
                  reason: r.reason || r.particulars || "",
                  amount: Number(r.amount) || 0,
                }))
              );
            } else if (rowsRaw && typeof rowsRaw === "object") {
              setUserRows(
                Object.values(rowsRaw).map((r: any) => ({
                  reason: (r as any).reason || (r as any).particulars || "",
                  amount: Number((r as any).amount) || 0,
                }))
              );
            }
          }
          const usedSpec = data.valuation_testing?.auditor_used_specialist?.used_specialist;
          if (usedSpec === "Yes" || usedSpec === "No") {
            setAuditorUsedSpecialist(usedSpec);
          }
        }

        // Load FS sub-line ledger options (Other Income / Other Expenses)
        if ((window as any).sharePointAPI?.downloadTrialBalanceMapping) {
          const mappingResult = await (window as any).sharePointAPI.downloadTrialBalanceMapping({
            includeAmounts: true,
          });
          if (mappingResult?.success && mappingResult?.data) {
            const mappingData = mappingResult.data;
            const TARGET_FS = new Set(["Other Income", "Other Expenses"]);
            const ledgerMap = new Map<string, number>();

            const parseDate = (dateStr: string): Date => {
              const [day, month, year] = dateStr.split("/");
              return new Date(
                2000 + parseInt(year, 10),
                parseInt(month, 10) - 1,
                parseInt(day, 10)
              );
            };

            for (const [, ledgers] of Object.entries(mappingData)) {
              if (!Array.isArray(ledgers)) continue;
              for (const item of ledgers as any[]) {
                if (!item) continue;
                if (!TARGET_FS.has(item.fs_sub_line)) continue;
                const amountDict = item.amount || {};
                const dates = Object.keys(amountDict || {});
                if (!dates.length) continue;
                const latestDate = dates.reduce((a, b) =>
                  parseDate(a) > parseDate(b) ? a : b
                );
                const latestAmount = parseFloat(String(amountDict[latestDate])) || 0;
                let ledgerName = (item.ledger_name || item.ledgerName || "").toString();
                ledgerName = ledgerName
                  .replace(/_x000D/g, "")
                  .replace(/\n/g, "")
                  .replace(/_+$/, "")
                  .trim();
                if (!ledgerName) continue;
                ledgerMap.set(ledgerName, (ledgerMap.get(ledgerName) || 0) + latestAmount);
              }
            }

            const options: LedgerOption[] = Array.from(ledgerMap.entries())
              .map(([name, amount]) => ({ name, amount }))
              .sort((a, b) => a.name.localeCompare(b.name));
            setFsOptions(options);
          }
        }
      } catch {
        // ignore, UI will show partial data if any
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const addUserRow = () => {
    setUserRows([...userRows, { reason: "", amount: 0 }]);
  };

  const updateUserRow = (index: number, field: "reason" | "amount", value: string) => {
    const updated = [...userRows];
    if (field === "reason") {
      updated[index] = { ...updated[index], reason: value };
    } else {
      updated[index] = { ...updated[index], amount: Number(value) || 0 };
    }
    setUserRows(updated);
  };

  const removeUserRow = (index: number) => {
    setUserRows(userRows.filter((_, i) => i !== index));
  };

  const saveValuation = async () => {
    try {
      setSaveStatus("saving");
      const result = await (window as any).sharePointAPI?.downloadClientJson?.(
        "Execution_BAPB_BioAst.json"
      );
      let current: any = result?.success && result?.data ? result.data : {};

      const gl = current.valuation_testing?.gain_loss_in_pnl || {};
      const baseGain = Number(gl.gain_loss || 0);
      let computedDiff = gl.difference || 0;
      if (numericDifference != null && !Number.isNaN(baseGain)) {
        computedDiff = numericDifference;
      }

      const serializedUserRows = userRows.map((r) => ({
        reason: r.reason,
        amount: r.amount,
      }));

      const updatedGainLoss = {
        ...gl,
        closing_balance: gl.closing_balance,
        opening_balance: gl.opening_balance,
        acquisitions: gl.acquisitions,
        sale_or_disposal: gl.sale_or_disposal,
        gain_loss: gl.gain_loss,
        gain_loss_tb:
          selectedLedgers.length > 0
            ? selectedLedgers.join(", ")
            : gl.gain_loss_tb || "",
        difference: computedDiff,
        user_rows: serializedUserRows,
        formula: gl.formula || "",
      };

      current.valuation_testing = current.valuation_testing || {};
      current.valuation_testing.auditor_used_specialist = {
        used_specialist: auditorUsedSpecialist || "",
      };
      current.valuation_testing.gain_loss_in_pnl = updatedGainLoss;

      if (current.disclosure_testing && typeof current.disclosure_testing === "object") {
        delete current.disclosure_testing.disclosures_checklist;
      }

      const jsonString = JSON.stringify(current, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const uploadRes = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName: "Execution_BAPB_BioAst.json",
        referenceValue: "Execution BAPB BioAst valuation testing",
        folderName: "juggernaut",
        fyYear: undefined,
        sectionList: ["Execution_BAPB_BioAst"],
      });

      if (!uploadRes || !uploadRes.success) {
        throw new Error(uploadRes?.error || "Upload failed");
      }

      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2500);
    } catch (e) {
      console.error("Failed to save BAPB valuation testing:", e);
      setSaveStatus("error");
    }
  };

  const gainLoss = gainLossData?.gain_loss ?? null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">
            Valuation Testing – Biological Assets other than Bearer Plants
          </h2>
          <p className="text-sm text-white/60">
            Testing the valuation of biological assets at fair value less costs to sell
          </p>
        </div>
      </div>

      <Card className="border-white/10 bg-white/5 text-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calculator className="h-5 w-5 text-emerald-400" />
            Auditor’s Specialist
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
              <Label className="text-sm text-white">
                Has the auditor used a specialist to ascertain the estimates and valuation disclosed
                by the management?
              </Label>
              <div className="mt-2 flex gap-3">
                <Button
                  type="button"
                  variant={auditorUsedSpecialist === "Yes" ? "default" : "outline"}
                  className={
                    auditorUsedSpecialist === "Yes"
                      ? "bg-emerald-600 text-white hover:bg-emerald-700"
                      : "border-white/20 text-white hover:bg-white/10"
                  }
                  onClick={() => setAuditorUsedSpecialist("Yes")}
                >
                  Yes
                </Button>
                <Button
                  type="button"
                  variant={auditorUsedSpecialist === "No" ? "default" : "outline"}
                  className={
                    auditorUsedSpecialist === "No"
                      ? "bg-emerald-600 text-white hover:bg-emerald-700"
                      : "border-white/20 text-white hover:bg-white/10"
                  }
                  onClick={() => setAuditorUsedSpecialist("No")}
                >
                  No
                </Button>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-white/20 text-white hover:bg-white/10"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-white/5 text-white">
        <CardHeader>
          <CardTitle className="text-lg">Gain/(loss) included in profit and loss</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-white/70">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading valuation data...</span>
            </div>
          )}

          {!loading && gainLossData && (
            <>
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-white/10">
                    <th className="border border-white/10 px-4 py-2 text-left">Particulars</th>
                    <th className="border border-white/10 px-4 py-2 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-white/10 px-4 py-2">
                      Closing Balance as per Register
                    </td>
                    <td className="border border-white/10 px-4 py-2 text-right">
                      {gainLossData.closing_balance?.toLocaleString() ?? "—"}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-white/10 px-4 py-2">Less: Opening Balance</td>
                    <td className="border border-white/10 px-4 py-2 text-right">
                      {gainLossData.opening_balance?.toLocaleString() ?? "—"}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-white/10 px-4 py-2">Less: Acquisitions</td>
                    <td className="border border-white/10 px-4 py-2 text-right">
                      {gainLossData.acquisitions?.toLocaleString() ?? "—"}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-white/10 px-4 py-2">
                      Add: Sale or Disposal (mortality)
                    </td>
                    <td className="border border-white/10 px-4 py-2 text-right">
                      {gainLossData.sale_or_disposal?.toLocaleString() ?? "—"}
                    </td>
                  </tr>
                  <tr className="bg-white/10 font-medium">
                    <td className="border border-white/10 px-4 py-2">Gain/(loss)</td>
                    <td className="border border-white/10 px-4 py-2 text-right">
                      {gainLoss != null ? gainLoss.toLocaleString() : "—"}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-white/10 px-4 py-2">
                      Gain/(loss) as per TB
                    </td>
                    <td className="border border-white/10 px-4 py-2 text-right">
                      <div className="relative inline-block w-full text-left">
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full justify-between bg-black/40 border-white/20 text-white hover:bg-white/10"
                          onClick={() => setIsLedgerDropdownOpen((open) => !open)}
                        >
                          <span className="truncate">
                            {selectedLedgers.length === 0
                              ? "Select ledger(s)"
                              : `${selectedLedgers.length} selected`}
                          </span>
                        </Button>
                        {isLedgerDropdownOpen && (
                          <div className="absolute right-0 z-20 mt-2 w-80 rounded-md border border-white/20 bg-black/90 shadow-lg max-h-64 overflow-auto">
                            {fsOptions.map((opt) => {
                              const checked = selectedLedgers.includes(opt.name);
                              return (
                                <button
                                  key={opt.name}
                                  type="button"
                                  onClick={() => {
                                    setSelectedLedgers((prev) =>
                                      checked
                                        ? prev.filter((n) => n !== opt.name)
                                        : [...prev, opt.name]
                                    );
                                  }}
                                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-white/10"
                                >
                                  <div className="flex items-center gap-2">
                                    <span
                                      className={`h-4 w-4 rounded border ${
                                        checked
                                          ? "bg-emerald-500 border-emerald-400"
                                          : "border-white/40"
                                      }`}
                                    />
                                    <span>{opt.name}</span>
                                  </div>
                                  <span className="text-xs text-white/60">
                                    {opt.amount.toLocaleString()}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-white/10 px-4 py-2">Difference</td>
                    <td className="border border-white/10 px-4 py-2 text-right">
                      {numericDifference != null ? numericDifference.toLocaleString() : "—"}
                    </td>
                  </tr>
                  <tr className="bg-white/5">
                    <td className="border border-white/10 px-4 py-2 text-white font-medium">
                      User reconciliation rows
                    </td>
                    <td className="border border-white/10 px-4 py-2 text-right text-white">
                      —
                    </td>
                  </tr>
                  {userRows.map((row, index) => (
                    <tr key={index}>
                      <td className="border border-white/10 px-4 py-2">
                        <Input
                          value={row.reason}
                          onChange={(e) => updateUserRow(index, "reason", e.target.value)}
                          className="border-white/10 bg-black/40 text-white"
                          placeholder="Particulars"
                        />
                      </td>
                      <td className="border border-white/10 px-4 py-2">
                        <Input
                          type="number"
                          value={row.amount || ""}
                          onChange={(e) => updateUserRow(index, "amount", e.target.value)}
                          className="border-white/10 bg-black/40 text-white text-right"
                          placeholder="Amount"
                        />
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-white/5 font-medium">
                    <td className="border border-white/10 px-4 py-2 text-white">Net difference</td>
                    <td className="border border-white/10 px-4 py-2 text-right text-white">
                      {netDifference != null ? netDifference.toLocaleString() : "—"}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addUserRow}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Row
                </Button>
                <Button
                  onClick={saveValuation}
                  disabled={saveStatus === "saving"}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {saveStatus === "saving" ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save
                    </>
                  )}
                </Button>
                {saveStatus === "saved" && (
                  <span className="text-green-400 text-sm">
                    Saved to Execution_BAPB_BioAst.json
                  </span>
                )}
                {saveStatus === "error" && (
                  <span className="text-red-400 text-sm">Save failed</span>
                )}
              </div>
            </>
          )}

          {!loading && !gainLossData && (
            <p className="text-sm text-white/60">
              No valuation data found in Execution_BAPB_BioAst.json. Run IPE Testing first.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
